import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api, setSession } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Shield, User, KeyRound, Phone } from "lucide-react";

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState("customer"); // customer | driver | admin
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const m = q.get("mode");
    if (m === "admin" || m === "driver" || m === "customer") setMode(m);
  }, [loc.search]);
  const [li, setLi] = useState({ email: "", password: "" });
  const [rg, setRg] = useState({ name: "", email: "", mobile: "", password: "", referred_by_code: "" });
  const [ad, setAd] = useState({ email: "", password: "" });
  const [drvTab, setDrvTab] = useState("pin"); // pin | password
  const [drvPin, setDrvPin] = useState({ mobile: "", pin: "" });
  const [drvPw, setDrvPw] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fp, setFp] = useState({ step: 1, email: "", otp: "", new_password: "", confirm: "" });

  const fpRequest = async () => {
    if (!fp.email) { toast.error("Enter your admin email"); return; }
    try {
      const r = await api.post("/auth/forgot-password/request", { email: fp.email });
      toast.success(r.data?.demo_otp ? `OTP sent (demo: ${r.data.demo_otp})` : "OTP sent to your WhatsApp");
      setFp(s => ({ ...s, step: 2 }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send OTP"); }
  };
  const fpReset = async () => {
    if (fp.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (fp.new_password !== fp.confirm) { toast.error("Passwords do not match"); return; }
    try {
      await api.post("/auth/forgot-password/reset", { email: fp.email, otp: fp.otp, new_password: fp.new_password });
      toast.success("Password updated — sign in with your new password");
      setForgotOpen(false);
      setFp({ step: 1, email: "", otp: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  useEffect(() => {
    const ref = new URLSearchParams(loc.search).get("ref");
    if (ref) setRg(r => ({ ...r, referred_by_code: ref.toUpperCase() }));
  }, [loc.search]);

  const login = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await api.post("/auth/login", li); setSession(r.data.token, r.data.user); toast.success("Welcome back!"); nav("/dashboard"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };
  const register = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await api.post("/auth/register", rg); setSession(r.data.token, r.data.user); toast.success("Account created!"); nav("/dashboard"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Registration failed"); }
    setLoading(false);
  };
  const adminLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const r = await api.post("/auth/admin-login", ad); setSession(r.data.token, r.data.user); toast.success("Welcome, Admin!"); nav("/admin"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };
  const driverLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = drvTab === "pin" ? { mobile: drvPin.mobile, pin: drvPin.pin } : { email: drvPw.email, password: drvPw.password };
      const r = await api.post("/auth/driver-login", payload);
      setSession(r.data.token, { ...r.data.driver, role: "driver" });
      toast.success(`Welcome, ${r.data.driver.name}`);
      nav("/driver");
    } catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };

  const modeIdx = { customer: 0, driver: 1, admin: 2 }[mode];
  const modeAccent = { customer: "bg-[#ea580c]", driver: "bg-amber-500", admin: "bg-[#1e1b4b]" }[mode];

  return (
    <PublicLayout>
      <section className="max-w-md mx-auto px-4 pt-6 pb-8">
        <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">Account</div>
        <h1 className="text-4xl font-display font-black tracking-tight">Sign in</h1>
        <p className="text-slate-600 mt-2 text-sm">One page for customers and the operations team.</p>

        {/* Sliding pill toggle — 3 modes */}
        <div className="mt-6 relative p-1 rounded-full bg-slate-100 border border-slate-200 grid grid-cols-3 text-sm font-semibold" data-testid="login-mode-toggle">
          <div
            className={`absolute top-1 bottom-1 w-[calc(33.333%-3px)] rounded-full shadow transition-all duration-300 ease-out ${modeAccent}`}
            style={{ left: `calc(${modeIdx * 33.333}% + 2px)` }}
            aria-hidden
          />
          <button
            type="button"
            onClick={() => setMode("customer")}
            data-testid="login-mode-customer"
            className={`relative z-10 py-2 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "customer" ? "text-white" : "text-slate-700"}`}
          >
            <User className="w-4 h-4" /> Customer
          </button>
          <button
            type="button"
            onClick={() => setMode("driver")}
            data-testid="login-mode-driver"
            className={`relative z-10 py-2 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "driver" ? "text-white" : "text-slate-700"}`}
          >
            <KeyRound className="w-4 h-4" /> Driver
          </button>
          <button
            type="button"
            onClick={() => setMode("admin")}
            data-testid="login-mode-admin"
            className={`relative z-10 py-2 rounded-full inline-flex items-center justify-center gap-1.5 transition-colors ${mode === "admin" ? "text-white" : "text-slate-700"}`}
          >
            <Shield className="w-4 h-4" /> Admin
          </button>
        </div>

        {/* Sliding panel — 3 slides */}
        <div className="mt-6 overflow-hidden rounded-2xl bg-white border border-slate-200 shadow-sm">
          <div
            className="flex w-[300%] transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${modeIdx * 33.333}%)` }}
          >
            {/* Customer panel */}
            <div className="w-1/3 p-6" data-testid="customer-panel">
              <Tabs defaultValue="login">
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
                </TabsList>
                <TabsContent value="login">
                  <button type="button" onClick={() => {
                    /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
                    const redirectUrl = window.location.origin + '/dashboard';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }} className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid="customer-google-signin-btn">
                    <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.5 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.3 13.1 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/><path fill="#FBBC05" d="M10.3 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.7-3.6-13.7-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                    Continue with Google
                  </button>
                  <div className="flex items-center gap-3 my-3 text-[11px] text-slate-400"><span className="h-px flex-1 bg-slate-200"/>or email &amp; password<span className="h-px flex-1 bg-slate-200"/></div>
                  <form onSubmit={login} className="space-y-4" data-testid="login-form">
                    <div><Label>Email</Label><Input required type="email" value={li.email} onChange={e => setLi({ ...li, email: e.target.value })} data-testid="login-email" /></div>
                    <div><Label>Password</Label><PasswordInput required value={li.password} onChange={e => setLi({ ...li, password: e.target.value })} data-testid="login-password" /></div>
                    <Button disabled={loading} type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="login-submit">{loading ? "…" : "Login"}</Button>
                  </form>
                </TabsContent>
                <TabsContent value="register">
                  <button type="button" onClick={() => {
                    /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
                    const redirectUrl = window.location.origin + '/dashboard';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }} className="w-full mt-4 flex items-center justify-center gap-2 py-2.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid="customer-google-signup-btn">
                    <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.5 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.3 13.1 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/><path fill="#FBBC05" d="M10.3 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.7-3.6-13.7-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                    Sign up with Google
                  </button>
                  <div className="flex items-center gap-3 my-3 text-[11px] text-slate-400"><span className="h-px flex-1 bg-slate-200"/>or register manually<span className="h-px flex-1 bg-slate-200"/></div>
                  <form onSubmit={register} className="space-y-4" data-testid="register-form">
                    <div><Label>Full Name</Label><Input required value={rg.name} onChange={e => setRg({ ...rg, name: e.target.value })} data-testid="reg-name" /></div>
                    <div><Label>Email</Label><Input required type="email" value={rg.email} onChange={e => setRg({ ...rg, email: e.target.value })} data-testid="reg-email" /></div>
                    <div><Label>Mobile</Label><Input required value={rg.mobile} onChange={e => setRg({ ...rg, mobile: e.target.value })} data-testid="reg-mobile" /></div>
                    <div><Label>Password</Label><PasswordInput required value={rg.password} onChange={e => setRg({ ...rg, password: e.target.value })} data-testid="reg-password" /></div>
                    <div><Label>Referral Code <span className="text-slate-400 font-normal">(optional)</span></Label><Input value={rg.referred_by_code} onChange={e => setRg({ ...rg, referred_by_code: e.target.value.toUpperCase() })} placeholder="e.g. SHRU-A3B7C1" data-testid="reg-referral" /></div>
                    <Button disabled={loading} type="submit" className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="reg-submit">{loading ? "…" : "Create account"}</Button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>

            {/* Driver panel */}
            <div className="w-1/3 p-6" data-testid="driver-panel">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-500 text-white grid place-items-center"><KeyRound className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-[#1e1b4b]">Driver Login</div>
                  <div className="text-xs text-slate-500">Your assigned trips at a glance</div>
                </div>
              </div>
              <div className="mt-3 flex gap-1 p-1 rounded-full bg-slate-100 text-xs font-semibold">
                <button type="button" onClick={() => setDrvTab("pin")} className={`flex-1 py-1.5 rounded-full inline-flex items-center justify-center gap-1 ${drvTab==="pin"?"bg-white shadow text-slate-900":"text-slate-500"}`} data-testid="driver-subtab-pin"><Phone className="w-3 h-3" /> Mobile + PIN</button>
                <button type="button" onClick={() => setDrvTab("password")} className={`flex-1 py-1.5 rounded-full inline-flex items-center justify-center gap-1 ${drvTab==="password"?"bg-white shadow text-slate-900":"text-slate-500"}`} data-testid="driver-subtab-password"><KeyRound className="w-3 h-3" /> Email + Password</button>
              </div>
              <form onSubmit={driverLogin} className="space-y-3 mt-4" data-testid="driver-login-form">
                {drvTab === "pin" ? (
                  <>
                    <div><Label>Mobile</Label><Input required inputMode="numeric" value={drvPin.mobile} onChange={e => setDrvPin({ ...drvPin, mobile: e.target.value })} placeholder="+91…" data-testid="driver-mobile" /></div>
                    <div><Label>4-digit PIN</Label><Input required inputMode="numeric" maxLength={4} pattern="[0-9]{4}" value={drvPin.pin} onChange={e => setDrvPin({ ...drvPin, pin: e.target.value.replace(/\D/g, "") })} data-testid="driver-pin" /></div>
                  </>
                ) : (
                  <>
                    <div><Label>Email</Label><Input required type="email" value={drvPw.email} onChange={e => setDrvPw({ ...drvPw, email: e.target.value })} data-testid="driver-email" /></div>
                    <div><Label>Password</Label><PasswordInput required value={drvPw.password} onChange={e => setDrvPw({ ...drvPw, password: e.target.value })} data-testid="driver-password" /></div>
                  </>
                )}
                <Button disabled={loading} type="submit" className="w-full bg-amber-500 hover:bg-amber-600" data-testid="driver-login-submit">{loading ? "Signing in…" : "Sign in as Driver"}</Button>
              </form>
              <p className="text-[11px] text-slate-400 mt-3 text-center">Don't have a PIN? Ask the office to send you one on WhatsApp.</p>
            </div>

            {/* Admin panel */}
            <div className="w-1/3 p-6" data-testid="admin-panel">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#1e1b4b] text-white grid place-items-center"><Shield className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold text-[#1e1b4b]">Admin Login</div>
                  <div className="text-xs text-slate-500">Operations & fleet management</div>
                </div>
              </div>
              <form onSubmit={adminLogin} className="space-y-4 mt-4" data-testid="admin-login-form">
                <button type="button" onClick={() => {
                  /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
                  const redirectUrl = window.location.origin + '/dashboard';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid="google-signin-btn">
                  <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.5 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.3 13.1 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/><path fill="#FBBC05" d="M10.3 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.7-3.6-13.7-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                  Continue with Google
                </button>
                <div className="text-center text-[11px] text-slate-400">— or admin email/password —</div>
                <div><Label>Email</Label><Input required type="email" value={ad.email} onChange={e => setAd({ ...ad, email: e.target.value })} data-testid="admin-email" placeholder="admin@shrutitt.com" /></div>
                <div><Label>Password</Label><PasswordInput required value={ad.password} onChange={e => setAd({ ...ad, password: e.target.value })} data-testid="admin-password" /></div>
                <div className="flex justify-end -mt-2">
                  <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-[#ea580c] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
                </div>
                <Button disabled={loading} type="submit" className="w-full bg-[#1e1b4b] hover:bg-[#14205a]" data-testid="admin-login-submit">{loading ? "Signing in…" : "Sign in as Admin"}</Button>
              </form>
              <p className="text-[11px] text-slate-400 mt-3 text-center">Restricted to authorised operators only.</p>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader><DialogTitle>Reset Admin Password</DialogTitle></DialogHeader>
          {fp.step === 1 ? (
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-sm font-semibold">Admin email</label>
                <Input value={fp.email} onChange={e => setFp({ ...fp, email: e.target.value })} placeholder="admin@shrutitt.com" data-testid="fp-email" />
              </div>
              <p className="text-xs text-slate-500">An OTP will be sent to the WhatsApp number linked to this admin account.</p>
              <Button onClick={fpRequest} className="w-full bg-[#ea580c] hover:bg-orange-700" data-testid="fp-request">Send OTP</Button>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-sm font-semibold">6-digit OTP</label>
                <Input inputMode="numeric" maxLength={6} value={fp.otp} onChange={e => setFp({ ...fp, otp: e.target.value.replace(/[^0-9]/g, "") })} data-testid="fp-otp" />
              </div>
              <div>
                <label className="text-sm font-semibold">New password</label>
                <PasswordInput value={fp.new_password} onChange={e => setFp({ ...fp, new_password: e.target.value })} data-testid="fp-new" />
              </div>
              <div>
                <label className="text-sm font-semibold">Confirm new password</label>
                <PasswordInput value={fp.confirm} onChange={e => setFp({ ...fp, confirm: e.target.value })} data-testid="fp-confirm" />
              </div>
              <Button onClick={fpReset} className="w-full bg-[#1e1b4b] hover:bg-[#14205a]" data-testid="fp-reset">Reset password</Button>
              <button type="button" onClick={() => setFp(s => ({ ...s, step: 1 }))} className="text-xs text-slate-500 hover:underline">Use a different email</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
